// useAgentWorkspaces — lists the operator's Agent Workspace rooms
// (sq_agent_workspaces) via the existing v23 endpoint, and creates one
// on demand. A workspace is required to create or read PR-style action
// requests (sq_agent_action_requests) — see api/square/agent-workspace/
// workspaces.ts and action-requests.ts.
//
// This hook does not invent a workspace concept: sq_agent_workspaces
// already exists and is already consumed elsewhere (Agent Workspace
// surface, Operations Agent Command's read-only queue). Non-admin users
// only ever see their own workspaces — server-enforced in
// workspaces.ts's GET handler (`role.level < 80` scopes to
// `created_by = user.id`) — this hook reflects that honestly rather
// than assuming a shared room exists.

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError, isForbiddenError } from '../../../api/client'

export interface AgentWorkspaceSummary {
  id: string
  name: string
  created_at: string
}

export type AgentWorkspaceLoadState = 'loading' | 'ready' | 'auth_required' | 'forbidden' | 'error'

export interface AgentWorkspacesState {
  state: AgentWorkspaceLoadState
  workspaces: AgentWorkspaceSummary[]
  errorMessage: string | null
  creating: boolean
  createError: string | null
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

const INITIAL_STATE: AgentWorkspacesState = {
  state: 'loading',
  workspaces: [],
  errorMessage: null,
  creating: false,
  createError: null,
}

export function useAgentWorkspaces() {
  const [s, setS] = useState<AgentWorkspacesState>(INITIAL_STATE)

  const load = useCallback(async () => {
    setS((prev) => ({ ...prev, state: 'loading', errorMessage: null }))
    try {
      const { workspaces } = await api.agentWorkspace.workspaces.list()
      setS((prev) => ({ ...prev, state: 'ready', workspaces: workspaces || [] }))
    } catch (err) {
      if (isAuthError(err)) return setS((prev) => ({ ...prev, state: 'auth_required' }))
      if (isForbiddenError(err)) return setS((prev) => ({ ...prev, state: 'forbidden' }))
      setS((prev) => ({ ...prev, state: 'error', errorMessage: formatError(err) }))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const createWorkspace = useCallback(async (name: string): Promise<AgentWorkspaceSummary | null> => {
    setS((prev) => ({ ...prev, creating: true, createError: null }))
    try {
      const { workspace } = await api.agentWorkspace.workspaces.create(name)
      setS((prev) => ({
        ...prev,
        creating: false,
        state: 'ready',
        workspaces: [workspace, ...prev.workspaces],
      }))
      return workspace
    } catch (err) {
      setS((prev) => ({ ...prev, creating: false, createError: formatError(err) }))
      return null
    }
  }, [])

  return { ...s, reload: load, createWorkspace }
}
